/**
 * The 10:00 call card, in the layout the customer asked for (14 October, section 4): one result at 10:00
 * (PAPER BULL PUT or NO TRADE, since no rule has been validated), the reason, the trade (strikes, distance,
 * width, executable and mid credit, most it can make and lose, return on risk), the separate chances of an
 * upward and a downward 1% touch in comparable past sessions with their ranges, the bull put's past results
 * in those sessions, a position-size calculator, and the strike watch after entry.
 * Data: GET /v1/ten-am (backend/ten_am.py, backend/paper_bullput.py, backend/ten_am_history.json).
 */
import { useEffect, useState } from "react";
import { Badge, StatCell, TextField, fmt, etAndLocal } from "@/sentinel";

const isNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
const pct1 = (v) => (isNum(v) ? `${(Number(v) * 100).toFixed(1)}%` : "—");
const pct2 = (v) => (isNum(v) ? `${(Number(v) * 100).toFixed(2)}%` : "—");
const pts = (v) => (isNum(v) ? Number(v).toFixed(2) : "—");
const usd = (v) => (isNum(v) ? `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—");
const susd = (v) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}$${Math.abs(Number(v)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const level = (v) => (isNum(v) ? fmt(Number(v), "price") : "—");

const DAY_TYPE = {
  event: "a release before 10:00",
  at10: "a 10:00 release (result not known at 10:00)",
  fomc: "an FOMC day (decision at 14:00)",
  ordinary: "no material release",
};

export function classKey(rec, t) {
  const dt = t?.flags?.day_type || (rec?.fomc_day ? "fomc" : null);
  if (!dt || !rec?.classification) return null;
  return { dt, kept: rec.classification === "KEPT", key: `${dt}|${rec.classification === "KEPT" ? "kept" : "excluded"}` };
}

function Decision({ rec, t }) {
  let label = "Waiting for 10:00";
  let tone;
  let reason = `The call is made at ${etAndLocal("10:01:15", { day: rec?.day })}, once the 10:00 minute has closed.`;
  if (rec && t) {
    if (t.status === "open" || t.status === "settled") {
      label = "PAPER BULL PUT";
      tone = "brand";
      reason = "The frozen rule's conditions are met: not an FOMC day, and the credit at the 10:00 bid and ask is at least 0.10.";
    } else {
      label = "NO TRADE";
      reason = t.status === "no data" ? `Missing data: ${t.reason}. The day is not counted.` : `${t.reason}.`;
    }
  } else if (rec && !isNum(rec.ref)) {
    label = "NO TRADE";
    reason = "No 10:00 level was recorded.";
  }
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="call-decision">
      <div className="sn-row" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--text-xl, 1.35rem)", fontWeight: 700, color: tone ? "var(--text-primary)" : "var(--text-secondary)" }}
          data-testid="call-label">{label}</span>
        <Badge icon="FlaskConical">Not validated · paper only · never traded</Badge>
      </div>
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="call-reason">{reason}</p>
    </div>
  );
}

function Trade({ t }) {
  if (!t || !(t.status === "open" || t.status === "settled")) return null;
  const midS = isNum(t.short_quote?.bid) ? (t.short_quote.bid + t.short_quote.ask) / 2 : null;
  const midL = isNum(t.long_quote?.bid) ? (t.long_quote.bid + t.long_quote.ask) / 2 : null;
  const mid = isNum(midS) && isNum(midL) ? midS - midL : null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="call-trade">
      <p className="sn-small" style={{ margin: 0 }}>
        Sell the <b>{t.short}</b> put, buy the <b>{t.long}</b> put: {pct2(-t.distance)} below the 10:00 level of {level(t.S)}, {t.W} points wide.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Credit at the bid and ask" value={`${pts(t.credit)} (${usd(t.credit * 100)})`}
          sub={`mid ${pts(mid)} · short ${pts(t.short_quote?.bid)}/${pts(t.short_quote?.ask)} · long ${pts(t.long_quote?.bid)}/${pts(t.long_quote?.ask)}`} />
        <StatCell size="sm" label="Most it can make" value={usd(t.max_gain)} sub="per spread, after $3 of costs" />
        <StatCell size="sm" label="Most it can lose" value={usd(-t.max_loss)} sub="per spread" />
        {t.status === "settled"
          ? <StatCell size="sm" label="Result" value={susd(t.net)} sub={`official close ${level(t.settlement)}`} data-testid="call-net" />
          : <StatCell size="sm" label="Return on risk if it expires worthless" value={pct1(t.ror_if_win)} />}
      </div>
    </div>
  );
}

function Context({ rec, t }) {
  const f = t?.flags;
  const parts = [];
  if (rec?.classification) parts.push(rec.classification === "KEPT" ? "the quiet-day filter keeps this session" : "the quiet-day filter excludes this session");
  if (f && !f.error) {
    parts.push(f.event_day ? `${f.lead} came out before 10:00${isNum(f.lead_z) ? `, ${Number(f.lead_z) >= 0 ? "+" : "−"}${Math.abs(Number(f.lead_z)).toFixed(1)} standard deviations from consensus` : ""}${f.major_surprise ? " (a major surprise)" : ""}` : `day type: ${DAY_TYPE[f.day_type] || f.day_type}`);
  }
  if (rec?.events_after?.length) parts.push(`still due: ${rec.events_after.map((e) => `${etAndLocal(e.time_et, { day: rec.day })} ${e.event}`).join(", ")}`);
  if (!parts.length) return null;
  return <p className="sn-small" style={{ margin: 0 }} data-testid="call-context">Known at 10:00: {parts.join("; ")}.</p>;
}

function Comparable({ rec, t, hist }) {
  const k = classKey(rec, t);
  if (!k || !hist?.touch) return null;
  const tr = hist.touch[k.key];
  const bp = hist.bull_put?.by_class?.[k.key];
  const range = (ci) => (ci ? `${pct1(ci[0])} to ${pct1(ci[1])}` : "");
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="call-comparable">
      <p className="sn-small" style={{ margin: 0 }}>
        <b>Sessions like this one</b> ({DAY_TYPE[k.dt]}, {k.kept ? "kept" : "excluded"} by the quiet-day filter), described from the past, not forecast:
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Touched +1% after 10:00" value={tr ? pct1(tr.up / tr.sessions) : "—"} sub={tr ? `${tr.up} of ${tr.sessions} · range ${range(tr.up_ci)}` : null} data-testid="call-up" />
        <StatCell size="sm" label="Touched −1% after 10:00" value={tr ? pct1(tr.down / tr.sessions) : "—"} sub={tr ? `${tr.down} of ${tr.sessions} · range ${range(tr.down_ci)}` : null} data-testid="call-down" />
        <StatCell size="sm" label="This bull put, past trades" value={bp ? `${susd(bp.mean)} each` : "—"}
          sub={bp ? `${bp.trades} trades, ${pct1(bp.win_rate)} won${bp.ci95 ? `, range ${susd(bp.ci95[0])} to ${susd(bp.ci95[1])}` : ""}` : null} />
        <StatCell size="sm" label="Worst past trade" value={bp ? usd(bp.worst) : "—"} sub={bp ? `median credit ${pts(bp.median_credit)}` : null} />
      </div>
      <p className="sn-small sn-faint" style={{ margin: 0 }}>
        Touches since {tr?.since || "October 2021"}; bull put results since {hist.bull_put?.since || "March 2022"}, held to the official close
        at the 10:00 bid and ask. Every period is included, so these describe the past and have not been tested as a forecast.
      </p>
    </div>
  );
}

function readSizing() {
  try { return JSON.parse(localStorage.getItem("sn.sizing") || "{}"); } catch { return {}; }
}

export function Sizing({ t, finalPeriod }) {
  const saved = readSizing();
  const [acct, setAcct] = useState(saved.acct ?? "100000");
  const [risk, setRisk] = useState(saved.risk ?? "2");
  useEffect(() => { try { localStorage.setItem("sn.sizing", JSON.stringify({ acct, risk })); } catch { /* private window */ } }, [acct, risk]);
  const a = Number(acct), r = Number(risk);
  const maxLoss = t && isNum(t.max_loss) ? Number(t.max_loss) : null;
  const n = maxLoss && a > 0 && r > 0 ? Math.floor((a * r) / 100 / maxLoss) : null;
  const f = finalPeriod || {};
  const scaled = (v) => (isNum(v) && n ? `${usd(v * n)} (${pct1(Math.abs(v * n) / a)} of the account)` : "—");
  return (
    <details className="sn-stack" data-testid="call-sizing">
      <summary className="sn-small" style={{ cursor: "pointer" }}>Position size against capital at risk</summary>
      <div className="sn-stack" style={{ gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Account size ($)" type="number" inputMode="decimal" value={acct} onChange={(e) => setAcct(e.target.value)} data-testid="sizing-acct" />
          <TextField label="Most you will lose on one trade (% of account)" type="number" inputMode="decimal" value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="sizing-risk" />
        </div>
        {maxLoss ? (
          <p className="sn-small" style={{ margin: 0 }} data-testid="sizing-result">
            {n > 0
              ? <>At most <b>{n}</b> spread{n === 1 ? "" : "s"}: a full loss would be {usd(-maxLoss * n)} ({pct1((maxLoss * n) / a)} of the account).</>
              : <>This spread can lose up to {usd(maxLoss)} per spread, more than that budget allows: no position.</>}
            {n > 0 && <> At that size, the final test period's worst trade would have been {scaled(f.largest_loss)}, its worst month {scaled(f.worst_month_net)} and its deepest drawdown {scaled(f.max_drawdown)}.</>}
          </p>
        ) : <p className="sn-small sn-muted" style={{ margin: 0 }}>Shown when there is a paper trade to size.</p>}
        <p className="sn-small sn-faint" style={{ margin: 0 }}>Kept in this browser only. A calculator, not a recommendation to trade.</p>
      </div>
    </details>
  );
}

function Watch({ t }) {
  const w = t?.watch;
  if (!w || t.status !== "open") return null;
  return (
    <p className="sn-small" style={{ margin: 0 }} data-testid="call-watch">
      Strike watch at {etAndLocal(w.at_et, { day: t.day })}: the index was {level(w.spx)}, {pct2(w.distance)} {w.distance >= 0 ? "above" : "below"} the {t.short} strike.
      {w.alerts?.length ? ` Alerts today: ${w.alerts.join("; ")}.` : ""} Information only: the frozen rule holds to the official close.
    </p>
  );
}

export function TenAmCallCard({ rec, t, paper }) {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)" }} data-testid="call-card">
      <Decision rec={rec} t={t} />
      <Trade t={t} />
      <Watch t={t} />
      <Context rec={rec} t={t} />
      <Comparable rec={rec} t={t} hist={paper?.history} />
      {(t?.status === "open" || t?.status === "settled") && <Sizing t={t} finalPeriod={paper?.final_period} />}
    </div>
  );
}
